import React, { useState, useEffect, useCallback, useRef } from 'react';
import {useTranslation} from 'react-i18next';import {FolderOpen, Plus, ChevronLeft, Loader2, Trash2, Pencil, FolderInput, X} from 'lucide-react';import {Button} from '@/components/ui/button';import {ScrollArea} from '@/components/ui/scroll-area';import {AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle} from '@/components/ui/alert-dialog';import SwipeableBookmarkItem from './SwipeableBookmarkItem';
import {API_URL as API} from '@/utils/apiConfig';import {toast} from 'sonner';import axios from 'axios';

// ── Context Menu (right-click desktop / long-press mobile) ──────────────────
function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="fixed z-[200] min-w-[160px] rounded-xl shadow-xl py-1.5 animate-in fade-in zoom-in-95 duration-150"
      style={{
        top: Math.min(y, window.innerHeight - 150),
        left: Math.min(x, window.innerWidth - 180),
        background: 'var(--theme-surface)',
        border: '1px solid var(--glass-border)',
      }}
      data-testid="context-menu"
    >
      {items.map((item, i) => (
        <button
          key={i}
          onClick={() => { item.action(); onClose(); }}
          className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm transition-colors hover:bg-[var(--theme-hover)]"
          style={{ color: item.destructive ? '#ef4444' : 'var(--theme-text)' }}
          data-testid={`ctx-${item.testId || item.label.toLowerCase().replace(/\s/g, '-')}`}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ── Inline Rename Input ─────────────────────────────────────────────────────
function InlineRename({ value, onSave, onCancel }) {
  const [text, setText] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    else onCancel();
  };

  return (
    <input
      ref={inputRef}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleSubmit();
        if (e.key === 'Escape') onCancel();
      }}
      className="w-full bg-transparent text-sm font-medium outline-none border-b-2 py-0.5"
      style={{ color: 'var(--theme-text)', borderColor: 'var(--theme-accent)' }}
      maxLength={50}
      data-testid="inline-rename-input"
    />
  );
}

// ── Move to Group Modal (exported for reuse) ────────────────────────────────
export function MoveToGroupModal({ open, groups, currentGroupId, onMove, onClose }) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full sm:max-w-sm mx-auto rounded-t-2xl sm:rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
        style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="move-to-group-modal"
      >
        <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{t('bookmarks.move_to_group')}</h3>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('bookmarks.choose_group')}
          </p>
        </div>
        <div
          className="overflow-y-auto p-2"
          style={{ maxHeight: 'min(50vh, calc(var(--visual-vh, 100vh) - 200px))' }}
        >
          <button
            onClick={() => onMove(null)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors hover:bg-[var(--theme-hover)]"
            style={{ opacity: !currentGroupId ? 0.5 : 1 }}
            data-testid="move-to-ungrouped"
          >
            <FolderOpen className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            <span className="text-sm" style={{ color: 'var(--theme-text)' }}>{t('bookmarks.ungrouped')}</span>
            {!currentGroupId && <span className="text-[10px] ml-auto" style={{ color: 'var(--theme-textSecondary)' }}>{t('bookmarks.current')}</span>}
          </button>
          {groups.map((g) => (
            <button
              key={g.group_id}
              onClick={() => onMove(g.group_id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors hover:bg-[var(--theme-hover)]"
              style={{ opacity: g.group_id === currentGroupId ? 0.5 : 1 }}
              data-testid={`move-to-${g.group_id}`}
            >
              <FolderOpen className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
              <span className="text-sm" style={{ color: 'var(--theme-text)' }}>{g.name}</span>
              {g.group_id === currentGroupId && <span className="text-[10px] ml-auto" style={{ color: 'var(--theme-textSecondary)' }}>{t('bookmarks.current')}</span>}
            </button>
          ))}
          {groups.length === 0 && (
            <p className="text-xs text-center py-4" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('bookmarks.no_groups_hint')}
            </p>
          )}
        </div>
        <div className="px-5 py-3 border-t" style={{ borderColor: 'var(--glass-border)' }}>
          <Button variant="ghost" size="sm" onClick={onClose} className="w-full text-xs h-8 rounded-full">
            {t('bookmarks.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main BookmarkGroups Component ────────────────────────────────────────────
export default function BookmarkGroups({ onLoadConversation }) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupBookmarks, setGroupBookmarks] = useState([]);
  const [loadingBookmarks, setLoadingBookmarks] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [moveBookmark, setMoveBookmark] = useState(null);
  const longPressTimer = useRef(null);
  const createInputRef = useRef(null);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookmark-groups`, { withCredentials: true });
      setGroups(res.data.groups || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  useEffect(() => {
    if (showCreateForm) {
      setTimeout(() => createInputRef.current?.focus(), 100);
    }
  }, [showCreateForm]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/bookmark-groups`, { name }, { withCredentials: true });
      setGroups((prev) => [...prev, { ...res.data, bookmark_count: 0 }]);
      setNewName('');
      setShowCreateForm(false);
      toast.success(t('bookmarks.group_created'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bookmarks.failed_create_group'));
    }
    setCreating(false);
  };

  const handleRename = async (groupId, newNameVal) => {
    try {
      await axios.put(`${API}/bookmark-groups/${groupId}`, { name: newNameVal }, { withCredentials: true });
      setGroups((prev) => prev.map((g) => g.group_id === groupId ? { ...g, name: newNameVal } : g));
      if (activeGroup?.group_id === groupId) setActiveGroup((prev) => ({ ...prev, name: newNameVal }));
      toast.success(t('bookmarks.group_renamed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bookmarks.failed_rename'));
    }
    setRenamingId(null);
  };

  const handleDelete = async (groupId) => {
    try {
      await axios.delete(`${API}/bookmark-groups/${groupId}`, { withCredentials: true });
      setGroups((prev) => prev.filter((g) => g.group_id !== groupId));
      if (activeGroup?.group_id === groupId) setActiveGroup(null);
      toast.success(t('bookmarks.group_deleted'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bookmarks.failed_delete'));
    }
    setDeleteTarget(null);
  };

  const openGroup = async (group) => {
    setActiveGroup(group);
    setLoadingBookmarks(true);
    try {
      const res = await axios.get(`${API}/bookmark-groups/${group.group_id}/bookmarks`, { withCredentials: true });
      setGroupBookmarks(res.data.bookmarks || []);
    } catch {
      setGroupBookmarks([]);
    }
    setLoadingBookmarks(false);
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    try {
      await axios.delete(`${API}/bookmarks/${bookmarkId}`, { withCredentials: true });
      setGroupBookmarks((prev) => prev.filter((b) => b.bookmark_id !== bookmarkId));
      toast.success(t('bookmarks.bookmark_removed'));
    } catch { toast.error(t('bookmarks.failed_remove')); }
  };

  const handleMoveBookmark = async (bookmarkId, targetGroupId) => {
    try {
      await axios.put(`${API}/bookmarks/${bookmarkId}/group`, { group_id: targetGroupId }, { withCredentials: true });
      setGroupBookmarks((prev) => prev.filter((b) => b.bookmark_id !== bookmarkId));
      toast.success(t('bookmarks.bookmark_moved'));
      fetchGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bookmarks.failed_move'));
    }
    setMoveBookmark(null);
  };

  // Long-press handler for mobile
  const startLongPress = (e, group) => {
    longPressTimer.current = setTimeout(() => {
      const touch = e.touches?.[0];
      const x = touch?.clientX || e.clientX || 100;
      const y = touch?.clientY || e.clientY || 100;
      setContextMenu({
        x, y, group,
        items: [
          { label: t('bookmarks.rename'), icon: <Pencil className="h-3.5 w-3.5" />, action: () => setRenamingId(group.group_id) },
          { label: t('bookmarks.delete'), icon: <Trash2 className="h-3.5 w-3.5" />, action: () => setDeleteTarget(group), destructive: true },
        ],
      });
    }, 500);
  };

  const cancelLongPress = () => {
    clearTimeout(longPressTimer.current);
  };

  // Right-click handler for desktop
  const handleContextMenu = (e, group) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX, y: e.clientY, group,
      items: [
        { label: t('bookmarks.rename'), icon: <Pencil className="h-3.5 w-3.5" />, action: () => setRenamingId(group.group_id) },
        { label: t('bookmarks.delete'), icon: <Trash2 className="h-3.5 w-3.5" />, action: () => setDeleteTarget(group), destructive: true },
      ],
    });
  };

  // ── Inside a group view ──
  if (activeGroup) {
    return (
      <div className="flex flex-col flex-1 min-h-0" data-testid="group-detail-view">
        <div className="flex items-center gap-2 px-3 pt-2 pb-2 shrink-0">
          <button
            onClick={() => { setActiveGroup(null); fetchGroups(); }}
            className="p-1 rounded-md hover:bg-[var(--theme-hover)] transition"
            data-testid="back-to-groups"
          >
            <ChevronLeft className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
          <FolderOpen className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--theme-accent)' }} />
          <span className="text-xs font-semibold truncate" style={{ color: 'var(--theme-text)' }}>
            {activeGroup.name}
          </span>
          <span className="text-[10px] ml-auto" style={{ color: 'var(--theme-textSecondary)' }}>
            {groupBookmarks.length} {t('bookmarks.items_count', { count: groupBookmarks.length }).split(' ').slice(-1)[0]}
          </span>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-2">
            {loadingBookmarks ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : groupBookmarks.length === 0 ? (
              <div className="px-2 py-8 text-center">
                <FolderOpen className="h-8 w-8 mx-auto mb-2" style={{ color: 'var(--theme-textSecondary)', opacity: 0.3 }} />
                <p className="text-sm" style={{ color: 'var(--theme-textSecondary)', opacity: 0.6 }}>{t('bookmarks.no_bookmarks_in_group')}</p>
                <p className="text-xs mt-1.5 leading-relaxed" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }}>
                  {t('bookmarks.move_instruction')}
                </p>
              </div>
            ) : (
              groupBookmarks.map((bm) => (
                <div key={bm.bookmark_id} className="relative group/bm">
                  <SwipeableBookmarkItem
                    bookmark={bm}
                    onSelect={(convId, msgId) => onLoadConversation(convId, false, msgId)}
                    onDelete={handleDeleteBookmark}
                  />
                  <button
                    onClick={() => setMoveBookmark(bm)}
                    className="absolute top-2 right-2 p-1.5 rounded-lg transition-colors"
                    style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
                    title={t('bookmarks.move_to_different_group')}
                    data-testid={`move-bm-${bm.bookmark_id}`}
                  >
                    <FolderInput className="h-3 w-3" style={{ color: 'var(--theme-textSecondary)' }} />
                  </button>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        <MoveToGroupModal
          open={!!moveBookmark}
          groups={groups}
          currentGroupId={activeGroup.group_id}
          onMove={(gid) => handleMoveBookmark(moveBookmark.bookmark_id, gid)}
          onClose={() => setMoveBookmark(null)}
        />
      </div>
    );
  }

  // ── Groups list view ──
  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="bookmark-groups-view">
      {/* Create new group - button or form */}
      <div className="px-3 pt-2 pb-1 shrink-0">
        {showCreateForm ? (
          <div
            className="flex items-center gap-2 p-2 rounded-xl animate-in slide-in-from-top-2 duration-150"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
          >
            <input
              ref={createInputRef}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') { setShowCreateForm(false); setNewName(''); }
              }}
              placeholder={t('bookmarks.enter_group_name')}
              className="flex-1 bg-transparent text-xs outline-none py-0.5 placeholder:text-muted-foreground/50"
              style={{ color: 'var(--theme-text)' }}
              maxLength={50}
              data-testid="new-group-input"
            />
            <Button
              size="sm"
              className="h-7 rounded-full text-[10px] px-3"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              data-testid="create-group-submit-btn"
            >
              {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : t('bookmarks.create')}
            </Button>
            <button
              onClick={() => { setShowCreateForm(false); setNewName(''); }}
              className="p-0.5 rounded-md hover:bg-[var(--theme-hover)] transition"
            >
              <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowCreateForm(true)}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-colors hover:opacity-80"
            style={{
              background: 'var(--glass-bg)',
              border: '1px dashed var(--glass-border)',
              color: 'var(--theme-accent)',
            }}
            data-testid="create-group-btn"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('bookmarks.new_group')}
          </button>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : groups.length === 0 ? (
            <div className="px-2 py-6 text-center">
              <FolderOpen className="h-8 w-8 mx-auto mb-2" style={{ color: 'var(--theme-textSecondary)', opacity: 0.3 }} />
              <p className="text-sm" style={{ color: 'var(--theme-textSecondary)', opacity: 0.6 }}>{t('bookmarks.no_groups_yet')}</p>
              <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }}>
                {t('bookmarks.create_groups_hint')}
              </p>
            </div>
          ) : (
            groups.map((g) => (
              <div
                key={g.group_id}
                className="mb-1 rounded-xl border border-transparent hover:border-border bg-card hover:bg-muted/30 transition-colors cursor-pointer select-none"
                onClick={() => renamingId !== g.group_id && openGroup(g)}
                onContextMenu={(e) => handleContextMenu(e, g)}
                onTouchStart={(e) => startLongPress(e, g)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                data-testid={`group-item-${g.group_id}`}
              >
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <FolderOpen className="h-4 w-4 shrink-0" style={{ color: 'var(--theme-accent)' }} />
                  <div className="flex-1 min-w-0">
                    {renamingId === g.group_id ? (
                      <InlineRename
                        value={g.name}
                        onSave={(n) => handleRename(g.group_id, n)}
                        onCancel={() => setRenamingId(null)}
                      />
                    ) : (
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>
                        {g.name}
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] shrink-0 px-1.5 py-0.5 rounded-full" style={{ color: 'var(--theme-textSecondary)', background: 'var(--glass-bg)' }}>
                    {g.bookmark_count || 0}
                  </span>
                  <ChevronLeft className="h-3 w-3 shrink-0 rotate-180" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
                </div>
              </div>
            ))
          )}

          {groups.length > 0 && (
            <p className="text-[10px] text-center mt-3 px-4 leading-relaxed" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }}>
              {t('bookmarks.context_hint')}
            </p>
          )}
        </div>
      </ScrollArea>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bookmarks.delete_group_title', { name: deleteTarget?.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bookmarks.delete_group_desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('bookmarks.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget.group_id)} className="bg-red-600 hover:bg-red-700">
              {t('bookmarks.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
